import { Translations } from '@repo/types';
import { QuantityInputItem } from '../shared/types';

/**
 * Internal representation of room state
 */
export type RoomState = {
    adults: number;
    children: number;
};

/**
 * Child info for accommodation API (matches GuestInfo from MultipleRoomSearch)
 */
export type ChildInfo = {
    childId: number;
    age: string | null;
    bed: string | null;
};

/**
 * Guest configuration format for accommodation API (matches GuestInfo from MultipleRoomSearch)
 */
export type GuestConfig = {
    id: number;
    adults: number;
    children: ChildInfo[];
};

/**
 * Create default room state (1 room with 1 adult, 0 children)
 */
export const createDefaultRooms = (): RoomState[] => [{ adults: 1, children: 0 }];

/**
 * Update room count by adding/removing rooms
 */
export const updateRoomCount = (rooms: RoomState[], newCount: number): RoomState[] => {
    if (newCount > rooms.length) {
        // Add new rooms with defaults
        const newRooms = Array.from({ length: newCount - rooms.length }, () => ({
            adults: 1,
            children: 0,
        }));
        return [...rooms, ...newRooms];
    } else if (newCount < rooms.length) {
        // Remove rooms from end
        return rooms.slice(0, newCount);
    }
    return rooms;
};

/**
 * Update guest count for a specific room
 */
export const updateGuestCount = (
    rooms: RoomState[],
    roomIdx: number,
    guestType: 'adults' | 'children',
    value: number,
): RoomState[] => {
    return rooms.map((room, idx) => (idx === roomIdx ? { ...room, [guestType]: value } : room));
};

/**
 * Calculate effective max for a guest type based on personsMax constraint
 */
const getEffectiveMax = (personsMax: number | undefined, otherGuestCount: number): number => {
    return personsMax !== undefined ? personsMax - otherGuestCount : Infinity;
};

/**
 * Generate room count input item
 */
export const getRoomCountItem = (
    roomCount: number,
    translations: Translations,
): QuantityInputItem => ({
    id: 'rooms',
    value: roomCount,
    label: roomCount === 1 ? translations.rooms.singular : translations.rooms.plural,
    min: 1,
    max: Infinity,
});

/**
 * Generate guest input items for all rooms
 *
 * Applies personsMax constraint: adults.max = personsMax - children, children.max = personsMax - adults
 */
export const getGuestItemsPerRoom = (
    rooms: RoomState[],
    personsMax: number | undefined,
    translations: Translations,
): QuantityInputItem[][] => {
    return rooms.map((room) => [
        {
            id: 'adults',
            value: room.adults,
            label: room.adults === 1 ? translations.adult.singular : translations.adult.plural,
            min: 0,
            max: getEffectiveMax(personsMax, room.children),
        },
        {
            id: 'children',
            value: room.children,
            label: translations.children.plural,
            min: 0,
            max: getEffectiveMax(personsMax, room.adults),
        },
    ]);
};

/**
 * Calculate total guests across all rooms
 */
export const getTotalGuests = (rooms: RoomState[]): number => {
    return rooms.reduce((sum, room) => sum + room.adults + room.children, 0);
};

/**
 * Convert to API format (GuestConfig[])
 */
export const toApiFormat = (rooms: RoomState[]): GuestConfig[] => {
    return rooms.map((room, idx) => ({
        id: idx,
        adults: room.adults,
        children: Array.from({ length: room.children }, (_, childIdx) => ({
            childId: childIdx,
            age: null,
            bed: null,
        })),
    }));
};
